/**
 * Clearance Doc Annotation Service
 * 文件標注 — 匡列指位（框選來源區域 → 對應報關欄位）
 */

import api from '../api/axios';

export interface AnnotationBBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface DocAnnotation {
  id: number;
  sample_id: number;
  doc_type: string;
  page_number: number;
  bbox_x1: number;
  bbox_y1: number;
  bbox_x2: number;
  bbox_y2: number;
  extracted_text: string;
  field_code: string;
  sub_field: string | null;
  source_type: string;
  created_by: number | null;
  created_at: string;
}

export interface CreateAnnotationRequest {
  doc_type?: string;
  page_number?: number;
  bbox: AnnotationBBox;
  extracted_text: string;
  field_code: string;
  sub_field?: string | null;
  source_type?: string;
}

export interface UpdateAnnotationRequest {
  field_code?: string;
  sub_field?: string | null;
  extracted_text?: string;
}

export interface TrainingPair {
  sample_id: number;
  training_pair: Record<string, string | Record<string, string>>;
  annotation_count: number;
}

export interface DocumentListResponse {
  available: string[];
}

// 報關欄位定義（與後端 FIELD_DEFINITIONS 對齊）
// 對應 NX5105 GovernmentAgencyGoodsItem 欄34~46
export const CLEARANCE_FIELDS = [
  { code: 'field_34', name: '項次（34）',              subFields: [] as string[] },
  { code: 'field_35', name: '貨物名稱及規格（35）',    subFields: ['品名', 'P/N', 'LOT', 'DEV'] },
  { code: 'field_36', name: '生產國別（36）',          subFields: [] as string[] },
  { code: 'field_37', name: '輸出入許可文件（37）',    subFields: [] as string[] },
  { code: 'field_38', name: '稅則號別（38）',          subFields: [] as string[] },
  { code: 'field_39', name: '條件幣別金額（39）',      subFields: ['條件', '幣別', '金額'] },
  { code: 'field_40', name: '淨重公斤（40）',          subFields: [] as string[] },
  { code: 'field_41', name: '數量（41）',              subFields: ['數量', '單位'] },
  { code: 'field_42', name: '統計用數量（42）',        subFields: [] as string[] },
  { code: 'field_43', name: '完稅數量（43）',          subFields: [] as string[] },
  { code: 'field_44', name: '進口稅率（44）',          subFields: [] as string[] },
  { code: 'field_45', name: '納稅辦法（45）',          subFields: [] as string[] },
  { code: 'field_46', name: '貨物稅率（46）',          subFields: [] as string[] },
] as const;

export type ClearanceFieldCode = typeof CLEARANCE_FIELDS[number]['code'];

const BASE = '/api/ee/clearance/training';

export const clearanceAnnotationService = {
  /** 取此樣本有哪些文件類型可供標注 */
  listDocuments: (sampleId: number) =>
    api.get<DocumentListResponse>(`${BASE}/samples/${sampleId}/document-list`)
       .then(r => r.data),

  /** 取原始 PDF URL（直接用於 <iframe> 或 react-pdf） */
  getDocumentUrl: (sampleId: number, docType = 'commercial_invoice') =>
    `${BASE}/samples/${sampleId}/document?doc_type=${encodeURIComponent(docType)}`,

  /** 列出標注 */
  list: (sampleId: number) =>
    api.get<DocAnnotation[]>(`${BASE}/samples/${sampleId}/annotations`)
       .then(r => r.data),

  /** 新增標注 */
  create: (sampleId: number, data: CreateAnnotationRequest) =>
    api.post<DocAnnotation>(`${BASE}/samples/${sampleId}/annotations`, data)
       .then(r => r.data),

  /** 更新標注（修改欄位對應） */
  update: (annotationId: number, data: UpdateAnnotationRequest) =>
    api.put<DocAnnotation>(
      `/api/ee/clearance/training/annotations/${annotationId}`,
      data,
    ).then(r => r.data),

  /** 刪除標注 */
  remove: (annotationId: number) =>
    api.delete(`/api/ee/clearance/training/annotations/${annotationId}`),

  /** 產生訓練對（只含已框選欄位） */
  generatePair: (sampleId: number) =>
    api.post<TrainingPair>(`${BASE}/samples/${sampleId}/annotations/generate-pair`)
       .then(r => r.data),
};
